import { z } from 'zod';

const canonicalIterableDateTimeSchema = z.string().datetime({ offset: true, precision: 3 });
const legacyIterableDateTimeSchema = z.string().transform((value, context) => {
  const parts = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(?: ([+-]\d{2}:\d{2}))?$/.exec(value);
  if (!parts) {
    context.addIssue({ code: 'custom', message: 'Invalid Iterable date' });
    return z.NEVER;
  }

  const normalizedValue = `${parts[1]}T${parts[2]}${parts[3] ?? 'Z'}`;
  if (!z.string().datetime({ offset: true, precision: 0 }).safeParse(normalizedValue).success) {
    context.addIssue({ code: 'custom', message: 'Invalid Iterable date' });
    return z.NEVER;
  }

  return new Date(normalizedValue).toISOString();
});

const IterableDateTimeSchema = z.union([canonicalIterableDateTimeSchema, legacyIterableDateTimeSchema]);

export const IterableProfileSchema = z.object({
  userId: z.string(),
  redisIdentifier: z.string().nullish(),
  isRenewalsHoldout: z.boolean().nullish(),
  storeCountry: z.string().nullish(),
  country: z.string().nullish(),
  dateOfBirth: z.string().nullish(),
  monthOfBirth: z.number().nullish(),
  dayOfBirth: z.number().nullish(),
  activeStory: z.string().nullish(),
  email: z.string().nullish(),
  subscriptionProduct: z.string().nullish(),
  subscriptionPurchasedAt: z.string().nullish(),
  subscriptionStartedAtDate: z.string().nullish(),
  subscriptionEndDate: z.string().nullish(),
  emailVerified: z.boolean().nullish(),
  isCrmBackendControlGroup: z.boolean().nullish(),
  lifetimePurchase: z.boolean().nullish(),
  subscriptionStatus: z
    .enum(['active', 'active_cancelled', 'ended_cancelled', 'ended_expired', 'not subscribed'])
    .optional(),
  stripeSubscriptionProductSku: z.string().nullish(),
  subscriptionProvider: z.string().nullish(),
  lastScreen: z.string().nullish(),
  region: z.string().nullish(),
  installLocale: z.string().nullish(),
  locale: z.string().nullish(),
  // IANA timezone (e.g. "America/New_York"). Drives Iterable send-time optimization.
  // Historically populated by an external batch pipeline that was decommissioned; now sent by the app.
  timeZone: z.string().nullish(),
  version: z.string().nullish(),
  visitId: z.string().nullish(),
  visitorId: z.string().nullish(),
  reduceFreeChaptersABTestHasUnlockedChapter: z.boolean().nullish(),
  baseAnnualProductId: z.string().nullish(),
  lastActiveAtDate: z.string().nullish(),
  /** Rolling average launch hour (0-23, local time) over the last 14 sessions. Used for send-time optimisation in Iterable journeys. */
  avgLaunchHour: z.number().optional(),
  accountCreatedAt: z.string().nullish(),
  subscribedForMarketingEmails: z.boolean().nullish(),
  applicationBundleId: z.string().nullish(),
  // This variable is used as uppercase in Iterable
  Source: z.string().nullish(),
  // Debugging purposes, it's stripped out before sending to Iterable
  origin: z.string().nullish(),
  hasActiveUnlimitedEntitlement: z.boolean().optional(),
  'harlequin:hasActiveUnlimitedEntitlement': z.boolean().optional(),
  willUnlimitedEntitlementRenew: z.boolean().optional(),
  firstInstalledOnDate: IterableDateTimeSchema.nullish(),
  hasUsedStripeFreeTrial: z.boolean().optional(),
  activeUnlimitedEntitlement: z.record(z.string(), z.unknown()).nullish(),
  latestUnlimitedEntitlement: z.record(z.string(), z.unknown()).nullish(),
  /** @deprecated - in favor of `latestUnlimitedEntitlement` */
  lastUnlimitedEntitlement: z.record(z.string(), z.unknown()).nullish(),
  // ML propensity targeting (pushed daily by the near_real_time_recommendations batch jobs):
  // category is one of churn_target | churn_no_target | likely_subscriber | never_subscribe
  // (experiment control-group users are already masked upstream); scoredDate gates freshness.
  propensityCategory: z.string().nullish(),
  propensityScoredDate: z.string().nullish(),
  // DEV-5040 — story context for Iterable copy + wallet / AB-test attributes
  /** Variant the user is assigned to in the `testHarlequinPricing` Unleash AB-test (unset when not enrolled). */
  harlequinPricingTestVariant: z.string().nullish(),
  /** First story the user ever opened a chapter in, set once after install. `firstChapterStory` keeps the legacy id field name. */
  firstChapterStory: z.string().nullish(),
  firstChapterStoryTitle: z.string().nullish(),
  /** Title companion for the existing `activeStory` (most recent story opened). */
  activeStoryTitle: z.string().nullish(),
  /** Most recent story the user finished a chapter in. */
  lastChapterFinishedStory: z.string().nullish(),
  lastChapterFinishedStoryTitle: z.string().nullish(),
  /** Points wallet balance and whether it is currently empty (updated regularly). */
  pointsBalance: z.number().nullish(),
  walletEmpty: z.boolean().optional(),
});

export type IterableProfile = z.infer<typeof IterableProfileSchema>;
